import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import or from 'ember-truth-helpers/helpers/or';
import BvMenuItemContent from 'volta/components/bv-menu/item/content';
import BvUnstyledBtn from 'volta/components/bv-unstyled-btn';
import BvLink from 'volta/components/bv-link';
import classNames from 'volta/helpers/class-names';

import type { TOC } from '@ember/component/template-only';
import type { IBvMenuItem } from 'volta/components/bv-menu';

export interface BvMenuItemSignature {
  Args: IBvMenuItem & {
    onMouseEnter: (event: Event) => void;
  };
  Blocks: {
    default: [];
    prefix: [];
    suffix: [];
  };
  Element: HTMLLIElement;
}

export const ItemInnerClassName = 'bv-menu-item__inner';

const BvMenuItemComponent: TOC<BvMenuItemSignature> = <template>
  <li
    class={{classNames
      'bv-menu-item'
      (if @hasArrow 'bv-menu-item--has-arrow')
      (if @color (concat 'bv-menu-item--' @color))
      (if @selected 'bv-menu-item--selected')
    }}
    role='menuitem'
    tabindex='-1'
    disabled={{@disabled}}
    ...attributes
  >
    {{#let
      (component
        BvMenuItemContent
        hasArrow=@hasArrow
        isLoading=@isLoading
        active=@active
        href=@href
        model=@model
        icon=@icon
        iconColor=@iconColor
        content=@content
        suffix=@suffix
        color=@color
        selected=@selected
        onClick=@onClick
        disabled=@disabled
        tooltip=@tooltip
        title=@title
        badge=@badge
        prefix=@prefix
        shortcut=@shortcut
      )
      as |Content|
    }}
      {{#if @href}}
        {{! template-lint-disable no-nested-interactive }}
        {{#if @model}}
          <LinkTo
            @route={{@href}}
            @model={{@model}}
            class='bv-menu-item__inner'
            disabled={{@disabled}}
            {{on 'mouseenter' @onMouseEnter}}
          >
            <Content>
              <:prefix>
                {{yield to='prefix'}}
              </:prefix>
              <:default>
                {{yield}}
              </:default>
              <:suffix>
                {{yield to='suffix'}}
              </:suffix>
            </Content>
          </LinkTo>
        {{else}}
          <BvLink
            @href={{@href}}
            class='bv-menu-item__inner'
            disabled={{@disabled}}
            tabindex='0'
            {{on 'mouseenter' @onMouseEnter}}
          >
            <Content>
              <:prefix>
                {{yield to='prefix'}}
              </:prefix>
              <:default>
                {{yield}}
              </:default>
              <:suffix>
                {{yield to='suffix'}}
              </:suffix>
            </Content>
          </BvLink>
        {{/if}}
      {{else}}
        <BvUnstyledBtn
          class='bv-menu-item__inner'
          disabled={{or @disabled @isLoading}}
          @onClick={{optional @onClick}}
          {{on 'mouseenter' @onMouseEnter}}
        >
          <Content>
            <:prefix>
              {{yield to='prefix'}}
            </:prefix>
            <:default>
              {{yield}}
            </:default>
            <:suffix>
              {{yield to='suffix'}}
            </:suffix>
          </Content>
        </BvUnstyledBtn>
      {{/if}}
    {{/let}}
  </li>
</template>;

export default BvMenuItemComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvMenu::Item': typeof BvMenuItemComponent;
    'bv-menu/item': typeof BvMenuItemComponent;
  }
}
